import { useState, useCallback } from 'react';
import { animate } from '../api/API';
import { useTaskPolling } from './useTaskPolling';

/**
 * Custom hook for video animation logic (animating images into videos)
 *
 * @param {Object} options - Hook options
 * @param {string} options.token - Auth token
 * @param {Object} options.user - Current user object (null if not logged in)
 * @param {Object} options.selectedImage - Selected image data { type: 'sample'|'user', url, sampleIdx?, mediaId? }
 * @param {string} options.action - Animation action type (e.g., 'cowgirl', 'missionary')
 * @param {Object} options.callbacks - Callback functions
 *   - setGenType(type): Set generation type ('video'|null)
 *   - setGenerated(bool): Set generated state
 *   - setVideo(url): Set video result
 *   - setTaskId(id): Set task ID
 *   - setShowLoginPopup(bool): Show login popup
 *   - onScrollToResults(): Scroll to results area
 * @returns {Object} - { handleAnimate, error, setError, loading }
 */
export function useVideoAnimate({
    token,
    user,
    selectedImage,
    action,
    callbacks
}) {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const {
        setGenType,
        setGenerated,
        setVideo,
        setTaskId,
        setShowLoginPopup,
        onScrollToResults
    } = callbacks;

    // Task polling for generation
    const { startPolling } = useTaskPolling(token, {
        onSuccess: (status) => {
            setGenType(null);
            setGenerated(true);
            setVideo(status.result.output.message);
        },
        onError: (errorStatus) => {
            setGenType(null);
            setError(`Generation failed: ${errorStatus}`);
        },
        onPollingError: () => {
            setGenType(null);
            setError("Error checking generation status. Please try again.");
        },
        onAuthError: () => {
            setGenType(null);
            setError("Session expired. Please refresh the page and log in again.");
        }
    });

    const handleAnimate = useCallback(async () => {
        setError(null);

        if (!user) {
            if (setShowLoginPopup) setShowLoginPopup(true);
            return;
        }

        if (!selectedImage) {
            setError("Please select an image first");
            return;
        }

        setLoading(true);
        setGenType("video");
        setGenerated(false);

        // Scroll to results area
        if (onScrollToResults) {
            onScrollToResults();
        }

        try {
            // For samples: taskId=null, sampleIdx=index
            // For user photos: taskId=mediaId (external_id), batchIdx=0, sampleIdx=null
            const [task, statusCode] = await animate(
                token,
                selectedImage.type === 'user' ? selectedImage.mediaId : null,  // taskId for user photos
                action,
                selectedImage.type === 'user' ? 0 : null,  // batchIdx=0 for user photos
                selectedImage.type === 'sample' ? selectedImage.sampleIdx : null
            );

            if (statusCode === 400) {
                setGenType(null);
                setError("Generation already in progress");
                setLoading(false);
                return;
            }

            if (statusCode === 402) {
                setGenType(null);
                setError("Not enough credits");
                setLoading(false);
                return;
            }

            if (statusCode === 401) {
                setGenType(null);
                setError("Please log in to generate");
                setLoading(false);
                return;
            }

            if (task.task_id) {
                if (setTaskId) setTaskId(task.task_id);
                startPolling(task.task_id);
            }

        } catch (err) {
            console.error("Error starting generation:", err);
            setGenType(null);
            setError("Failed to start generation. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [
        token,
        user,
        selectedImage,
        action,
        setGenType,
        setGenerated,
        setVideo,
        setTaskId,
        setShowLoginPopup,
        onScrollToResults,
        startPolling
    ]);

    return {
        handleAnimate,
        error,
        setError,
        loading
    };
}
